import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMedia } from 'react-media';
import Uppy from '@uppy/core';
import type { Assembly } from '@uppy/transloadit';
import Transloadit from '@uppy/transloadit';
import { useApolloClient } from '@apollo/client';
import type {
  CreateFileMutationResult,
  CreateFileMutationVariables,
  CreatePortCallFileMutationResult,
  CreatePortCallFileMutationVariables,
  DeleteFileMutationResult,
  DeleteFileMutationVariables,
  FileInput,
  PortCallsDocuments__PortCallFileFragment,
  UpdateFileMutationResult,
  UpdateFileMutationVariables,
} from '../../graphql';
import {
  CreateFileDocument,
  CreatePortCallFileDocument,
  DeleteFileDocument,
  DocumentType,
  UpdateFileDocument,
} from '../../graphql';
import {
  Box,
  Button,
  DeleteButton,
  Form,
  FormField,
  FormRow,
  FormSection,
  Input,
  Select,
  Text,
} from '../ui';
import LoadingSpinner from '../LoadingSpinner';

export const documentTypeOptions = [
  { value: DocumentType.SOF, label: 'SOF' },
  { value: DocumentType.BOL, label: 'BOL' },
  { value: DocumentType.OTHER, label: 'Other' },
  { value: DocumentType.CARGO_MANIFEST, label: 'Cargo manifest' },
  { value: DocumentType.NOTICE_OF_READINESS, label: 'Notice of readiness' },
  { value: DocumentType.CARGO_PLAN, label: 'Cargo plan' },
  { value: DocumentType.MATES_RECIPT, label: "Mate's receipt" },
  {
    value: DocumentType.FYTOSANITARY_CERTIFICATE,
    label: 'Fytosanitary certificate',
  },
  {
    value: DocumentType.DANGEROUS_CARGO_DECLARATION,
    label: 'Dangerous cargo declaration',
  },
  { value: DocumentType.MEASUREMENT_REPORT, label: 'Measurement report' },
  { value: DocumentType.SURVEY_REPORT, label: 'Survey report' },
  {
    value: DocumentType.CARGO_HOLD_INSPECTION_REPORT,
    label: 'Cargo hold inspection report',
  },
  { value: DocumentType.DAMAGE, label: 'Damage' },
  { value: DocumentType.CARGO_CLAIM, label: 'Cargo claim' },
  { value: DocumentType.T2L_CUSTOMS_APPROVAL, label: 'T2L Customs approval' },
];

export const documentTypeToAbbrev: { [K in DocumentType]: string } = {
  [DocumentType.SOF]: 'SOF',
  [DocumentType.BOL]: 'B/L',
  [DocumentType.OTHER]: 'Other',
  [DocumentType.CARGO_MANIFEST]: 'CM',
  [DocumentType.NOTICE_OF_READINESS]: 'NOR',
  [DocumentType.CARGO_PLAN]: 'CP',
  [DocumentType.MATES_RECIPT]: 'Mate',
  [DocumentType.FYTOSANITARY_CERTIFICATE]: 'FC',
  [DocumentType.DANGEROUS_CARGO_DECLARATION]: 'DCC',
  [DocumentType.MEASUREMENT_REPORT]: 'MR',
  [DocumentType.SURVEY_REPORT]: 'SR',
  [DocumentType.CARGO_HOLD_INSPECTION_REPORT]: 'CHIR',
  [DocumentType.DAMAGE]: 'DMG',
  [DocumentType.CARGO_CLAIM]: 'CC',
  [DocumentType.T2L_CUSTOMS_APPROVAL]: 'T2L',
};

export interface PortCallFilesProps {
  onSave?: () => void;
  portCallFiles: PortCallsDocuments__PortCallFileFragment[];
  portCallId: number;
  portName: string;
  userId: number;
  voyageName: string;
  readOnly?: boolean;
}

type SaveStatus = 'LOADING' | 'ERROR' | 'SUCCESS';

const authKey = process.env.TRANSLOADIT_AUTH_KEY || '';
const templateId = process.env.TRANSLOADIT_TEMPLATE_ID;
const removePortCallDocApi = process.env.REMOVE_PORT_CALL_DOC_API || '';

function useUppy(factory: () => Uppy): Uppy {
  if (typeof factory !== 'function') {
    throw new TypeError(
      'useUppy: expected a function that returns a new Uppy instance',
    );
  }

  const uppy = useRef<Uppy | undefined>(undefined);
  if (uppy.current === undefined) {
    uppy.current = factory();

    if (!(uppy.current instanceof Uppy)) {
      throw new TypeError(
        `useUppy: factory function must return an Uppy instance, got ${typeof uppy.current}`,
      );
    }
  }

  useEffect(() => {
    return () => {
      uppy.current?.close({ reason: 'unmount' });
      uppy.current = undefined;
    };
  }, [uppy]);

  return uppy.current;
}

const PortCallFiles = ({
  onSave,
  voyageName,
  portCallFiles,
  portCallId,
  portName,
  userId,
  readOnly,
}: PortCallFilesProps): JSX.Element => {
  const client = useApolloClient();

  const [selectedTypes, setSelectedTypes] = useState<Map<number, DocumentType>>(
    new Map(),
  );
  const [fileNotes, setFileNotes] = useState<Map<number, string>>(new Map());
  const [updateStatus, setUpdateStatus] = useState<
    Map<number, SaveStatus | undefined>
  >(new Map());

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [newFile, setNewFile] = useState<File | undefined>(undefined);
  const [newDocType, setNewDocType] = useState<DocumentType | ''>('');
  const [newNote, setNewNote] = useState<string>('');

  const [errorMsg, setErrorMsg] = useState('');
  const [createStatus, setCreateStatus] = useState<SaveStatus | undefined>(
    undefined,
  );

  const uppy = useUppy(() => {
    const instance = new Uppy({
      id: 'port_call_doc',
      autoProceed: true,
    });

    instance.use(Transloadit, {
      service: 'https://api2-eu-west-1.transloadit.com/',
      params: {
        auth: {
          key: authKey,
        },
        template_id: templateId,
      },
      waitForEncoding: true,
      limit: 0,
    });

    // Error handling
    instance.on('error', (error) => {
      console.error({ error });
      setErrorMsg(error.message);
      setCreateStatus('ERROR');
    });

    instance.on('upload-error', (file, error, response) => {
      console.error({ file, error, response });
      setErrorMsg(error.message);
      setCreateStatus('ERROR');
    });

    instance.on('complete', () => {
      instance.setState({ files: {}, currentUploads: {} });
    });

    return instance;
  });

  const onClear = useCallback(() => {
    setNewFile(undefined);
    setNewDocType('');
    setNewNote('');

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [setNewFile, setNewDocType, setNewNote, fileInputRef]);

  const onTransloditComplete = useCallback(
    async (assembly: Assembly) => {
      const upload = assembly.uploads[0];

      if (userId) {
        const fileDocument: FileInput = {
          uri: upload.ssl_url,
          userId: userId,
          type: newDocType === '' ? DocumentType.OTHER : newDocType,
          note: newNote,
        };

        const { data, errors } = await client.mutate<
          CreateFileMutationResult['data'],
          CreateFileMutationVariables
        >({
          mutation: CreateFileDocument,
          variables: {
            input: {
              file: {
                ...fileDocument,
              },
            },
          },
        });

        const fileId = data?.createFile?.file?.id;

        if (!errors && fileId) {
          const portCallFile = {
            fileId: fileId,
            portCallId: portCallId,
          };
          const { errors } = await client.mutate<
            CreatePortCallFileMutationResult['data'],
            CreatePortCallFileMutationVariables
          >({
            mutation: CreatePortCallFileDocument,
            variables: {
              input: {
                portCallFile: {
                  ...portCallFile,
                },
              },
            },
          });

          if (errors) {
            setCreateStatus('ERROR');
            return;
          }
        }
      } else {
        setCreateStatus('ERROR');
        return;
      }

      onSave?.();
      setCreateStatus('SUCCESS');
      onClear();
    },
    [client, newDocType, newNote, onClear, onSave, portCallId, userId],
  );

  const updateFile = async (fileId: number) => {
    setUpdateStatus(new Map(updateStatus).set(fileId, 'LOADING'));

    await client.mutate<
      UpdateFileMutationResult['data'],
      UpdateFileMutationVariables
    >({
      mutation: UpdateFileDocument,
      variables: {
        fileId,
        patch: {
          note: fileNotes.get(fileId),
          type: selectedTypes.get(fileId),
        },
      },
    });

    onSave?.();
    setUpdateStatus(new Map(updateStatus).set(fileId, 'SUCCESS'));
  };

  const onFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = event.target.files?.[0] ?? undefined;
    if (!file) return;

    setNewFile(file);
  };

  const onUploadHandler = () => {
    setCreateStatus('LOADING');

    if (!newFile) {
      setCreateStatus(undefined);
      return;
    }

    const newFileName = `${voyageName.replace(/\s+/g, '-')}-${newDocType}-${
      newFile.name
    }`;

    let canAdd = true;
    portCallFiles
      .filter((fileRelation) => fileRelation.portCallId === portCallId)
      .forEach(({ fileByFileId }) => {
        const existingFileName = fileByFileId?.uri.substring(
          fileByFileId?.uri.lastIndexOf('/') + 1,
          fileByFileId?.uri.length,
        );

        if (existingFileName === newFileName) {
          canAdd = false;
        }
      });

    if (!canAdd) {
      setCreateStatus('ERROR');
      setErrorMsg(
        `A file with same name and type already exists for this voyage!`,
      );
      return;
    }

    try {
      uppy.addFile({
        source: 'file input',
        name: newFileName,
        type: newFile.type,
        data: newFile,
      });
    } catch (err) {
      console.error('Error adding file to uppy', err);
      setErrorMsg(err.message);
      setCreateStatus('ERROR');
    }
  };

  const onDelete = async ({ fileId, key }: { fileId: number; key: string }) => {
    setUpdateStatus(new Map(updateStatus).set(fileId, 'LOADING'));

    try {
      await fetch(removePortCallDocApi, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key }),
      });

      await client.mutate<
        DeleteFileMutationResult['data'],
        DeleteFileMutationVariables
      >({
        mutation: DeleteFileDocument,
        variables: {
          input: {
            id: fileId,
          },
        },
      });

      setUpdateStatus(new Map(updateStatus).set(fileId, undefined));
      onSave?.();
    } catch (error) {
      console.error('error deleting file', error);
      setUpdateStatus(new Map(updateStatus).set(fileId, 'ERROR'));
    }
  };

  useEffect(() => {
    if (portCallFiles) {
      portCallFiles.forEach((fileRelation) => {
        const file = fileRelation.fileByFileId;

        if (file) {
          setSelectedTypes((types) => new Map(types.set(file.id, file.type)));
          setFileNotes((notes) => new Map(notes.set(file.id, file.note ?? '')));
        }
      });
    }
  }, [portCallFiles]);

  useEffect(() => {
    setCreateStatus(undefined);
    setUpdateStatus(new Map());
  }, [portCallId]);

  useEffect(() => {
    uppy.on('transloadit:complete', onTransloditComplete);

    return () => {
      uppy.off('transloadit:complete', onTransloditComplete);
    };
  }, [uppy, onTransloditComplete]);

  const isSmallScreen = useMedia({ query: '(max-width: 992px)' });

  return (
    <Form isForm={false} border="none">
      <FormSection title={`Documents for ${portName}`}>
        <Box mb={4}>
          {portCallFiles &&
            portCallFiles
              .filter((fileRelation) => fileRelation.portCallId === portCallId)
              .map((fileRelation) => {
                const file = fileRelation.fileByFileId;

                if (file) {
                  const fileName = file.uri.substring(
                    file.uri.lastIndexOf('/') + 1,
                    file.uri.length,
                  );
                  const uploadUser = file.userByUserId?.email;

                  return (
                    <FormRow key={file.uri} mb={3} alignItems="center">
                      <Box width={isSmallScreen ? '100%' : 500} mr={3}>
                        <Input
                          readOnly
                          disabled
                          defaultValue={`${fileName} (${uploadUser})`}
                        />
                      </Box>
                      <Box width={isSmallScreen ? '100%' : 200} mr={3}>
                        <Select
                          options={documentTypeOptions}
                          value={selectedTypes.get(file.id)}
                          disabled={readOnly}
                          onChange={(e) => {
                            if (file.id) {
                              setSelectedTypes(
                                new Map(
                                  selectedTypes.set(
                                    file.id,
                                    e.target.value as DocumentType,
                                  ),
                                ),
                              );
                            }
                          }}
                        />
                      </Box>
                      <Box width={308} mr={3}>
                        <Input
                          type="text"
                          placeholder="Note"
                          value={fileNotes.get(file.id)}
                          mr={3}
                          disabled={readOnly}
                          onChange={(e) => {
                            if (file.id) {
                              setFileNotes(
                                new Map(fileNotes.set(file.id, e.target.value)),
                              );
                            }
                          }}
                        />
                      </Box>
                      {!readOnly && (
                        <Box display="flex" flexDirection="row" flexGrow={0}>
                          <Button
                            variant="secondary"
                            onPress={() =>
                              window.open(
                                file.uri,
                                '_blank',
                                'noopener,noreferrer',
                              )
                            }
                            mr={3}
                          >
                            View
                          </Button>
                          <Button
                            variant="secondary"
                            onPress={() => file.id && updateFile(file.id)}
                            mr={3}
                          >
                            Save
                          </Button>
                        </Box>
                      )}
                      {!readOnly && (
                        <DeleteButton
                          onPress={() => {
                            // In S3 we will have something like `82/fa6b60a3534e65937c4d85ca29604f/test-pdf.pdf` as the key
                            const key = file.uri.substring(
                              file.uri.lastIndexOf('/') - 33, // Here 33 refers to a 33-character prefix used to avoid file name collisions in s3 bucket
                              file.uri.length,
                            );
                            onDelete({ fileId: file.id, key });
                          }}
                          mr={3}
                        />
                      )}
                      {updateStatus.get(file.id) === 'LOADING' ? (
                        <LoadingSpinner />
                      ) : updateStatus.get(file.id) === 'SUCCESS' ? (
                        <Text fontWeight="bold" color="#5fad09">
                          ✓ Saved successfully
                        </Text>
                      ) : updateStatus.get(file.id) === 'ERROR' ? (
                        <Text fontWeight="bold" color="#FC4A15">
                          ✗ Error saving
                        </Text>
                      ) : null}
                    </FormRow>
                  );
                } else {
                  return null;
                }
              })}
        </Box>
        <Box
          width={isSmallScreen ? '100%' : 960}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          p={3}
          borderStyle="dashed"
          borderWidth={1}
          borderColor="neutral.gray_250"
          bg="neutral.white_100"
        >
          <FormRow flexWrap={isSmallScreen ? 'wrap' : 'nowrap'}>
            <FormField
              label="New file"
              width={isSmallScreen ? '100%' : 260}
              flexShrink={0}
            >
              <Input
                ref={fileInputRef}
                type="file"
                multiple={false}
                disabled={readOnly}
                onChange={onFileInputChange}
              />
            </FormField>
            <FormField
              label="Document type"
              width={isSmallScreen ? '100%' : 220}
              flexShrink={0}
            >
              <Select
                options={documentTypeOptions}
                value={newDocType}
                disabled={readOnly}
                onChange={(e) => setNewDocType(e.target.value as DocumentType)}
                placeholder="Select document type"
              />
            </FormField>
            <FormField label="Note" flexGrow={1} mr={0}>
              <Input
                type="text"
                disabled={readOnly}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
            </FormField>
          </FormRow>
          {!readOnly && (
            <FormRow alignItems="center">
              <Button
                disabled={!newFile || !newDocType || createStatus === 'LOADING'}
                variant="secondary"
                onPress={onUploadHandler}
                mr={3}
              >
                Upload file
              </Button>
              <Button variant="secondary" onPress={onClear} mr={3}>
                Clear
              </Button>
              {createStatus === 'LOADING' ? (
                <LoadingSpinner />
              ) : createStatus === 'SUCCESS' ? (
                <Text fontWeight="bold" color="#5fad09">
                  ✓ Saved successfully
                </Text>
              ) : createStatus === 'ERROR' ? (
                <Text fontWeight="bold" color="#FC4A15">
                  ✗ Error saving : {errorMsg}
                </Text>
              ) : null}
            </FormRow>
          )}
        </Box>
      </FormSection>
    </Form>
  );
};

export default PortCallFiles;
